import { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { listDevices } from "./newBackendApi/newDevices";
import { registerDeveloper } from "./newBackendApi/developer";
import { ROUTES } from "../routes";
import { Link as RouterLink } from "react-router-dom";

const Home2 = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isDark = theme.palette.mode === "dark";

  // "Join as developer" returns here with ?dev_join=1 — tag the user (idempotent)
  // and strip the flag so a refresh doesn't re-run it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("dev_join") !== "1") return;
    registerDeveloper()
      .then(() => enqueueSnackbar("You're now registered as a Vecros developer.", { variant: "success" }))
      .catch(() => enqueueSnackbar("Couldn't complete developer registration — please try again.", { variant: "error" }))
      .finally(() => {
        params.delete("dev_join");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      });
  }, [enqueueSnackbar]);

  // Real device count for the one stat we can actually source. The other former
  // cards (API calls / alerts / uptime) and the activity feed had no backing
  // endpoint, so they were removed rather than shown as fabricated numbers.
  const [deviceCount, setDeviceCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    listDevices({ page_number: 1, page_size: 1 })
      .then((d) => !cancelled && setDeviceCount(d.count ?? 0))
      .catch(() => {}); // best-effort dashboard stat
    return () => {
      cancelled = true;
    };
  }, []);

  const colors = {
    page: isDark ? "#000000" : "#ffffff",
    surface: isDark ? "#000000" : "#ffffff",
    surfaceMuted: isDark ? "#05070b" : "#f8fafc",
    border: isDark ? "#1c1c1c" : "#e2e8f0",
    divider: isDark ? "#111111" : "#edf1f5",
    heading: isDark ? "#ffffff" : "#0f172a",
    text: isDark ? "#c8d8e8" : "#334155",
    label: isDark ? "#c5cbd3" : "#475569",
    muted: isDark ? "#3a5070" : "#64748b",
    value: isDark ? "#e8f0ff" : "#0f172a",
    accent: "#3a8eff",
  };

  const heroBackground = isDark
    ? "radial-gradient(circle at 85% 50%, rgba(16,55,129,.25) 0%, rgba(4,9,26,.95) 70%, #000 100%)"
    : "radial-gradient(circle at 85% 45%, rgba(58,142,255,.14) 0%, rgba(248,250,252,.8) 48%, #fff 78%)";

  const FolderIcon = () => (
    <svg width="26" height="22" viewBox="0 0 24 20" fill={colors.accent} style={{ flexShrink: 0 }}>
      <path d="M22 19H2a1 1 0 01-1-1V4a1 1 0 011-1h8l2 2h10a1 1 0 011 1v12a1 1 0 01-1 1z" />
    </svg>
  );

  const stats =
    deviceCount == null
      ? []
      : [
          {
            label: "REGISTERED DEVICES",
            value: String(deviceCount),
            sub: "in your fleet",
            subColor: colors.muted,
          },
        ];

  const quickAccess = [
    { name: "Documentation", desc: "Guides, references & tutorials", href: "https://docs.vecros.com/", external: true },
    { name: "Protected downloads", desc: "Firmware, SDKs & tools", href: ROUTES.downloads },
    { name: "Support", desc: "Help topics & issue reporting", href: "https://discuss.vecros.com/", external: true },
  ];

  const panelStyle = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    boxShadow: isDark ? "none" : "0 8px 24px rgba(15,23,42,.045)",
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: "100%",
        overflow: "auto",
        background: colors.page,
        color: colors.text,
        transition: "background-color .2s ease, color .2s ease",
      }}
    >
      <Box
        sx={{
          background: heroBackground,
          px: { xs: 3, md: 7 },
          py: { xs: 4, md: 5 },
          textAlign: "left",
          borderBottom: `1px solid ${colors.divider}`,
        }}
      >
        <Box
          component="h1"
          sx={{
            fontSize: { xs: 30, md: 38 },
            fontWeight: 500,
            color: colors.heading,
            lineHeight: 1.18,
            letterSpacing: ".08em",
            m: 0,
          }}
        >
          BUILD SMARTER.<br />
          FLY <Box component="span" sx={{ color: colors.accent }}>FURTHER.</Box>
        </Box>
        <Box component="p" sx={{ mt: 1.75, mb: 0, fontSize: 14, color: colors.muted, lineHeight: 1.65 }}>
          The complete platform for autonomous systems developers.<br />
          Ship firmware, manage fleets, and deploy AI agents — all from one place.
        </Box>
        <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Box
            component="button"
            onClick={() => window.open("https://docs.vecros.com/", "_blank")}
            sx={{
              background: "#1460cc", color: "#fff", border: 0, px: 3, py: 1.25,
              borderRadius: "5px", fontSize: 12, cursor: "pointer", fontWeight: 600,
              "&:hover": { background: "#0f55b8" },
            }}
          >
            Get Started
          </Box>
          <Box
            component="button"
            onClick={() => window.open("https://docs.vecros.com/", "_blank")}
            sx={{
              background: "transparent", color: colors.accent, border: `1px solid ${colors.accent}`,
              px: 3, py: 1.25, borderRadius: "5px", fontSize: 12, cursor: "pointer", fontWeight: 600,
              "&:hover": { background: "rgba(58,142,255,.08)" },
            }}
          >
            Read the docs
          </Box>
        </Box>
      </Box>

      {stats.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 2, p: 2.5 }}>
          {stats.map((stat) => (
            <Box key={stat.label} sx={{ ...panelStyle, p: "20px 24px", textAlign: "left" }}>
              <Box sx={{ fontSize: 11, color: colors.label, letterSpacing: ".12em" }}>{stat.label}</Box>
              <Box sx={{ mt: 1, color: colors.value, lineHeight: 1 }}>
                <Box component="span" sx={{ fontSize: 30, fontWeight: 600 }}>{stat.value.split("/")[0]}</Box>
                {stat.value.includes("/") && (
                  <Box component="span" sx={{ fontSize: 14, color: colors.muted }}>/{stat.value.split("/")[1]}</Box>
                )}
              </Box>
              <Box sx={{ mt: .75, fontSize: 11, color: stat.subColor }}>
                {stat.alert && <Box component="span" sx={{ mr: .5, fontWeight: 700 }}>↑</Box>}
                {stat.sub}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 2, px: 2.5, pt: 2.5, pb: 2.5 }}>
        <Box sx={{ ...panelStyle, p: "20px 22px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
            <Box sx={{ fontSize: 10, color: colors.muted, letterSpacing: ".12em" }}>QUICK ACCESS</Box>
            <Box sx={{ fontSize: 12, color: colors.accent }}>View All →</Box>
          </Box>
          {quickAccess.map((item, index) => (
            <Box
              key={item.name}
              {...(item.external
                ? { component: "a", href: item.href, target: "_blank", rel: "noopener noreferrer" }
                : { component: RouterLink, to: item.href })}
              sx={{
                display: "flex", alignItems: "center", gap: 1.75, py: 1.5, cursor: "pointer",
                textDecoration: "none", color: "inherit",
                borderBottom: index < quickAccess.length - 1 ? `1px solid ${colors.divider}` : 0,
                "&:hover": { opacity: .8 },
              }}
            >
              <FolderIcon />
              <Box sx={{ textAlign: "left" }}>
                <Box sx={{ fontSize: 14, color: colors.text }}>{item.name}</Box>
                <Box sx={{ fontSize: 11.5, color: colors.muted }}>{item.desc}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Home2;
